import { graphql } from 'gatsby';
import React from 'react';
import { css } from '@emotion/react';
import { colors } from '../../primitives/colors';
import Container from '../../primitives/grid/container';
import Link from '../../primitives/link';
import PageTitle from '../../primitives/page-title';
import apCaseOnlyTitleTags from '../../utils/ap-case-only-title-tags';
import paddingDefault from '../../utils/padding-default';
import RichTextEditorStyles from '../../utils/rich-text-editor-styles';
import { breakpoints } from '../../primitives/tokens';

const styles = {
    linkItem: css({
        color: colors.primaryOrange,
        textDecoration: 'underline',
    }),
    linkWrap: css({
        ':last-of-type': {
            marginLeft: '40px',
            [breakpoints.tb]: {
                marginLeft: 0,
            },
        },
    }),
    lineWrap: css({
        rowGap: '0',
        gridTemplateColumns: '630px 1fr 1fr',
        position: 'relative',
        display: 'grid',
        paddingTop: '36px',
        paddingBottom: '44px',
        fontSize: '20px',
        lineHeight: '24px',
        [breakpoints.tb]: {
            rowGap: '16px',
        },
        [breakpoints.mb]: {
            gridTemplateColumns: 'repeat(1, minmax(0, 1fr))',
            rowGap: '8px',
        },
        '::before,::after': {
            content: "''",
            height: '1px',
            backgroundColor: colors.primaryOrange,
            display: 'block',
            position: 'absolute',
            right: '0',
            left: '0',
        },
        '::before': {
            top: 0,
        },
        '::after': {
            bottom: '-1px',
        },
    }),
    copy: css({
        marginBottom: '64px',
        fontSize: '20px',
        lineHeight: '28px',
        a: {
            color: colors.primaryOrange,
            textDecoration: 'underline',
        },
    }),
    pageTitleWrap: css({
        marginBottom: '40px',
        maxWidth: '672px',
        lineHeight: 1,
    }),
    ingredientsList: css({
        fontSize: '18px',
        lineHeight: '28px',
        fontWeight: 600,
    }),
};

const IngredientsDisclosure: React.FC<Queries.IngredientsDisclosureDataFragment> = (data) => {
    const { heading, copy, ingredients } = data;

    return (
        <Container css={paddingDefault()}>
            <div css={styles.pageTitleWrap}>
                <PageTitle>{apCaseOnlyTitleTags(heading)}</PageTitle>
            </div>
            <div
                css={[RichTextEditorStyles, styles.copy]}
                /* eslint-disable-next-line react/no-danger */
                dangerouslySetInnerHTML={{ __html: copy || '' }}
            />
            {ingredients && (
                <ul css={styles.ingredientsList}>
                    {ingredients.map((ingredient) => {
                        if (!ingredient) return null;

                        const { label, disclosureFile, safetyDatasheetFile } = ingredient;

                        return (
                            <li css={styles.lineWrap} key={label}>
                                <p>{label}</p>
                                <div css={styles.linkWrap}>
                                    {disclosureFile?.url && (
                                        <Link css={styles.linkItem} to={disclosureFile.url}>
                                            Ingredients Disclosure
                                        </Link>
                                    )}
                                </div>
                                <div css={styles.linkWrap}>
                                    {safetyDatasheetFile?.url && (
                                        <Link css={styles.linkItem} to={safetyDatasheetFile.url}>
                                            Safety Data Sheet
                                        </Link>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </Container>
    );
};

export const query = graphql`
  fragment IngredientsDisclosureData on CsIngredientsDisclosure {
    ... on CsIngredientsDisclosure {
      heading
      copy
      ingredients {
        label
        disclosureFile {
          url
        }
        safetyDatasheetFile {
          url
        }
      }
    }
  }
`;

export default IngredientsDisclosure;
